import React, { useEffect, useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Tooltip,
    Button,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    OpenInNew as OpenInNewIcon,
    Architecture as CadIcon,
    PhoneAndroid as MobileIcon,
    Computer as DesktopIcon,
    Apps as AppsIcon
} from '@mui/icons-material';

interface DwgViewerProps {
    open: boolean;
    onClose: () => void;
    fileUrl: string;
    fileName: string;
}

const DwgViewer: React.FC<DwgViewerProps> = ({ open, onClose, fileUrl, fileName }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [downloading, setDownloading] = useState(false);
    const [autoOpened, setAutoOpened] = useState(false);
    const [tryingToOpenApp, setTryingToOpenApp] = useState(false);
    const [downloadedBlobUrl, setDownloadedBlobUrl] = useState<string | null>(null);
    const appOpenTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Detect platform
    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

    // Auto-open behavior when dialog opens
    useEffect(() => {
        if (open && !autoOpened) {
            setAutoOpened(true);

            if (isMobile) {
                // On mobile: Download file first, then try to open with app
                handleMobileOpen();
            } else {
                // On PC: Open DWG FastView web in new tab along with the download
                handleOpenDesktop();
            }
        }
    }, [open]);

    // Reset states when dialog closes
    useEffect(() => {
        if (!open) {
            setAutoOpened(false);
            setDownloading(false);
            setTryingToOpenApp(false);
            if (downloadedBlobUrl) {
                window.URL.revokeObjectURL(downloadedBlobUrl);
                setDownloadedBlobUrl(null);
            }
            if (appOpenTimeoutRef.current) {
                clearTimeout(appOpenTimeoutRef.current);
            }
        }
    }, [open]);

    // Download file and return blob URL
    const downloadFile = async (): Promise<string | null> => {
        setDownloading(true);
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();

            // Create blob URL
            const blobUrl = window.URL.createObjectURL(blob);
            setDownloadedBlobUrl(blobUrl);

            // Also trigger actual download so user has the file
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setDownloading(false);
            return blobUrl;
        } catch (error) {
            console.error('Error downloading file:', error);
            setDownloading(false);
            // Fallback: open URL directly
            window.open(fileUrl, '_blank');
            return null;
        }
    };

    // Mobile: Download and try to open with app
    const handleMobileOpen = async () => {
        const blobUrl = await downloadFile();

        if (blobUrl && isAndroid) {
            // On Android: Try to open file with intent
            // This will prompt the user to choose an app if multiple are available
            setTryingToOpenApp(true);

            // Create an intent URL for Android
            // This attempts to open the downloaded file with registered apps
            try {
                // Use a short delay to let the download complete
                setTimeout(() => {
                    // Try to trigger "Open with" dialog on Android
                    // by creating a link with download attribute removed
                    const intentLink = document.createElement('a');
                    intentLink.href = blobUrl;
                    intentLink.target = '_blank';
                    // Android should show "Open with" dialog for DWG files
                    // if an app like DWG FastView is installed
                    document.body.appendChild(intentLink);
                    intentLink.click();
                    document.body.removeChild(intentLink);

                    setTryingToOpenApp(false);
                }, 1000);
            } catch (e) {
                setTryingToOpenApp(false);
            }
        }
    };

    const handleOpenDesktop = async () => {
        await downloadFile();

        // Open DWG FastView web viewer in new tab
        setTimeout(() => {
            window.open('https://www.dwgfastview.com/online/', '_blank', 'noopener,noreferrer');
        }, 500);
    };

    // Try to open with app on mobile
    const handleTryOpenWithApp = () => {
        if (downloadedBlobUrl) {
            const link = document.createElement('a');
            link.href = downloadedBlobUrl;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Download first
            handleMobileOpen();
        }
    };

    const handleOpenMobileAppStore = () => {
        if (isIOS) {
            window.open('https://apps.apple.com/app/dwg-fastview-cad-viewer/id898445628', '_blank');
        } else {
            window.open('https://play.google.com/store/apps/details?id=com.gstarmc.android', '_blank');
        }
    };

    const handleManualDownload = () => {
        if (downloadedBlobUrl) {
            const link = document.createElement('a');
            link.href = downloadedBlobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            window.open(fileUrl, '_blank');
        }
    };

    const handleOpenWebViewer = () => {
        window.open('https://www.dwgfastview.com/online/', '_blank', 'noopener,noreferrer');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#1a237e',
                    color: 'white',
                    py: 2,
                    px: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CadIcon sx={{ fontSize: 32, color: '#00bcd4' }} />
                    <Box>
                        <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            Visor DWG
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'grey.300', mt: 0.25, fontSize: { xs: '0.7rem', sm: '0.875rem' } }} noWrap>
                            {fileName}
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Cerrar">
                    <IconButton onClick={onClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {(downloading || tryingToOpenApp) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={48} sx={{ color: '#1a237e', mb: 2 }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {downloading ? 'Descargando archivo...' : 'Intentando abrir con la app...'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            {downloading
                                ? (isMobile ? 'Se intentará abrir con DWG FastView' : 'Se abrirá DWG FastView Web')
                                : 'Si tienes DWG FastView instalado, se abrirá automáticamente'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {isMobile ? (
                            /* Mobile view */
                            <Box>
                                <Alert
                                    severity="info"
                                    sx={{ mb: 3, borderRadius: 2 }}
                                    icon={<MobileIcon />}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        Archivo descargado
                                    </Typography>
                                    <Typography variant="body2">
                                        Toca el botón de abajo para ver el archivo. Si tienes DWG FastView instalado, se abrirá automáticamente.
                                    </Typography>
                                </Alert>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<AppsIcon />}
                                    onClick={handleTryOpenWithApp}
                                    sx={{
                                        py: 1.5,
                                        bgcolor: '#00bcd4',
                                        '&:hover': { bgcolor: '#00acc1' },
                                        fontWeight: 600,
                                        fontSize: '1rem'
                                    }}
                                >
                                    Abrir con DWG FastView
                                </Button>

                                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                                    ¿No tienes la app instalada?
                                </Typography>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<DownloadIcon />}
                                    onClick={handleOpenMobileAppStore}
                                    sx={{
                                        py: 1.5,
                                        borderColor: '#1a237e',
                                        color: '#1a237e'
                                    }}
                                >
                                    Instalar DWG FastView ({isIOS ? 'App Store' : 'Play Store'})
                                </Button>

                                <Button
                                    variant="text"
                                    fullWidth
                                    startIcon={<OpenInNewIcon />}
                                    onClick={handleManualDownload}
                                    sx={{
                                        mt: 1.5,
                                        py: 1,
                                        color: 'text.secondary'
                                    }}
                                >
                                    Descargar archivo de nuevo
                                </Button>
                            </Box>
                        ) : (
                            /* Desktop view */
                            <Box>
                                <Alert
                                    severity="success"
                                    sx={{ mb: 3, borderRadius: 2 }}
                                    icon={<DesktopIcon />}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        ¡Archivo descargado!
                                    </Typography>
                                    <Typography variant="body2">
                                        Sube el archivo en DWG FastView Web para visualizarlo.
                                    </Typography>
                                </Alert>

                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                    Pasos para ver el archivo:
                                </Typography>
                                <Box component="ol" sx={{ pl: 2.5, '& li': { mb: 1.5 } }}>
                                    <li>
                                        <Typography variant="body2">
                                            En la pestaña de <strong>DWG FastView</strong> que se abrió, haz clic en "Open File"
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body2">
                                            Selecciona el archivo <strong>{fileName}</strong> de tus descargas
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body2">
                                            El archivo se mostrará en el visor CAD online
                                        </Typography>
                                    </li>
                                </Box>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<OpenInNewIcon />}
                                    onClick={handleOpenWebViewer}
                                    sx={{
                                        mt: 2,
                                        py: 1.5,
                                        bgcolor: '#1a237e',
                                        '&:hover': { bgcolor: '#0d1642' }
                                    }}
                                >
                                    Abrir DWG FastView Web
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<DownloadIcon />}
                                    onClick={handleManualDownload}
                                    sx={{
                                        mt: 1.5,
                                        py: 1.5,
                                        borderColor: '#1a237e',
                                        color: '#1a237e'
                                    }}
                                >
                                    Descargar archivo de nuevo
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DwgViewer;
